import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import styles from './PortalProvider.css';

interface PortalContextType {
    registerPortal: (id: string, content: ReactNode) => void;
    unregisterPortal: (id: string) => void;
}

const PortalContext = createContext<PortalContextType | null>(null);

export const usePortal = () => {
    const context = useContext(PortalContext);
    if (!context) {
        throw new Error('usePortal must be used within a PortalProvider');
    }
    return context;
};

interface PortalProviderProps {
    children: ReactNode;
}

export const PortalProvider = ({ children }: PortalProviderProps) => {
    const [portals, setPortals] = useState<Record<string, ReactNode>>({});

    const registerPortal = useCallback((id: string, content: ReactNode) => {
        setPortals(prev => ({ ...prev, [id]: content }));
    }, []);

    const unregisterPortal = useCallback((id: string) => {
        setPortals(prev => {
            const newPortals = { ...prev };
            delete newPortals[id];
            return newPortals;
        });
    }, []);

    return (
        <PortalContext.Provider value={{ registerPortal, unregisterPortal }}>
            {children}
            <div className={styles.portalContainer}>
                {Object.entries(portals).map(([id, content]) => (
                    <div key={id} className={styles.portalItem}>
                        {content}
                    </div>
                ))}
            </div>
        </PortalContext.Provider>
    );
};

export default PortalProvider;
